/**
 * CreateUserPage - Registration form for new referees.
 *
 * Allows judges to register new users during tournament day.
 * Creates both User and Referee records automatically.
 *
 * Fields:
 * - Username (required)
 * - Password (required, min 8 chars)
 * - First name (required)
 * - Last name (required)
 * - Email (required)
 * - Phone (required)
 * - Telegram (optional)
 * - Referee years (optional, default 0)
 */

import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Alert,
  InputAdornment,
  IconButton,
} from '@mui/material';
import BackIcon from '@mui/icons-material/ArrowBack';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useMutation } from '@tanstack/react-query';
import type { AxiosError } from 'axios';

import { usersApi, type CreateUserRequest } from '@/api/client';

// Validation helpers (matching legacy tuho3 rules)
const usernameRegex = /^[\w@.-]+$/;
const phoneRegex = /^[\d\s+-]*$/;

function validateUsername(value: string): string | null {
  if (!value) return 'Käyttäjätunnus on pakollinen';
  if (value.length < 3) return 'Käyttäjätunnus liian lyhyt (min 3 merkkiä)';
  if (!usernameRegex.test(value)) return 'Käyttäjätunnus voi sisältää vain kirjaimia, numeroita ja merkkejä @.-_';
  return null;
}

function validatePassword(value: string): string | null {
  if (!value) return 'Salasana on pakollinen';
  if (value.length < 8) return 'Salasanan tulee olla vähintään 8 merkkiä';
  if (/^\d+$/.test(value)) return 'Salasana ei voi koostua pelkästään numeroista';
  return null;
}

function validateEmail(value: string): string | null {
  if (!value) return 'Sähköposti on pakollinen';
  if (!value.includes('@') || !value.includes('.')) return 'Virheellinen sähköpostiosoite';
  return null;
}

function validatePhone(value: string): string | null {
  if (!value) return 'Puhelinnumero on pakollinen';
  if (!phoneRegex.test(value)) return 'Puhelinnumero voi sisältää vain numeroita, välilyöntejä ja merkkejä +-';
  return null;
}

function validateRequired(value: string, fieldName: string): string | null {
  if (!value.trim()) return `${fieldName} on pakollinen`;
  return null;
}

export function CreateUserPage() {
  // Form state
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [telegram, setTelegram] = useState('');
  const [refereeYears, setRefereeYears] = useState(0);

  // UI state
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [createdUser, setCreatedUser] = useState<{ username: string; password: string } | null>(null);

  // Create user mutation
  const createMutation = useMutation({
    mutationFn: (data: CreateUserRequest) => usersApi.create(data),
    onSuccess: () => {
      // Store credentials for display
      setCreatedUser({ username, password });
    },
    onError: (error: AxiosError<{ username?: string[]; email?: string[]; detail?: string }>) => {
      const data = error.response?.data;
      if (data?.username) {
        setErrors((prev) => ({ ...prev, username: data.username![0] }));
      }
      if (data?.email) {
        setErrors((prev) => ({ ...prev, email: data.email![0] }));
      }
    },
  });

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    const usernameError = validateUsername(username);
    if (usernameError) newErrors.username = usernameError;

    const passwordError = validatePassword(password);
    if (passwordError) newErrors.password = passwordError;

    if (password !== confirmPassword) {
      newErrors.confirmPassword = 'Salasanat eivät täsmää';
    }

    const firstNameError = validateRequired(firstName, 'Etunimi');
    if (firstNameError) newErrors.firstName = firstNameError;

    const lastNameError = validateRequired(lastName, 'Sukunimi');
    if (lastNameError) newErrors.lastName = lastNameError;

    const emailError = validateEmail(email);
    if (emailError) newErrors.email = emailError;

    const phoneError = validatePhone(phone);
    if (phoneError) newErrors.phone = phoneError;

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) return;

    createMutation.mutate({
      username,
      password,
      first_name: firstName,
      last_name: lastName,
      email,
      phone: phone || undefined,
      telegram: telegram || undefined,
      referee_years: refereeYears,
    });
  };

  // Success view - show credentials
  if (createdUser) {
    return (
      <Box sx={{ maxWidth: 500, mx: 'auto', pb: 4 }}>
        <Typography variant="h4" fontWeight={700} gutterBottom>
          Käyttäjä luotu!
        </Typography>

        <Alert severity="success" sx={{ mb: 3 }}>
          Käyttäjä <strong>{createdUser.username}</strong> on luotu onnistuneesti.
        </Alert>

        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Kirjautumistiedot
          </Typography>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Käyttäjätunnus:
            </Typography>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
              {createdUser.username}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Salasana:
            </Typography>
            <Typography variant="h6" fontWeight={600} fontFamily="monospace">
              {createdUser.password}
            </Typography>
          </Box>
        </Paper>

        <Alert severity="info" sx={{ mb: 3 }}>
          Muista kirjautumistiedot ja siirry kirjautumaan.
        </Alert>

        <Button
          variant="contained"
          fullWidth
          size="large"
          component={RouterLink}
          to="/login"
          sx={{ py: 1.5 }}
        >
          Siirry kirjautumaan
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 500, mx: 'auto', pb: 4 }}>
      <Button
        startIcon={<BackIcon />}
        component={RouterLink}
        to="/login"
        sx={{ mb: 2, minHeight: 44 }}
      >
        Takaisin kirjautumiseen
      </Button>

      <Typography variant="h4" fontWeight={700} gutterBottom>
        Luo uusi käyttäjä
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Luo uusi tuomaritunnus. Käyttäjälle luodaan automaattisesti tuomariprofiili.
      </Typography>

      {createMutation.isError && !Object.keys(errors).length && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Käyttäjän luonti epäonnistui. Tarkista tiedot ja yritä uudelleen.
        </Alert>
      )}

      <Paper sx={{ p: { xs: 2, sm: 3 } }}>
        <form onSubmit={handleSubmit}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <TextField
              label="Käyttäjätunnus"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              error={!!errors.username}
              helperText={errors.username || 'Kirjaimia, numeroita ja merkkejä @.-_'}
              fullWidth
              required
              autoComplete="off"
              InputProps={{ sx: { minHeight: 56 } }}
            />

            <TextField
              label="Salasana"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              error={!!errors.password}
              helperText={errors.password || 'Vähintään 8 merkkiä'}
              fullWidth
              required
              autoComplete="new-password"
              InputProps={{
                sx: { minHeight: 56 },
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              label="Salasana uudelleen"
              type={showConfirmPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              error={!!errors.confirmPassword}
              helperText={errors.confirmPassword}
              fullWidth
              required
              autoComplete="new-password"
              InputProps={{
                sx: { minHeight: 56 },
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              label="Etunimi"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              error={!!errors.firstName}
              helperText={errors.firstName}
              fullWidth
              required
              InputProps={{ sx: { minHeight: 56 } }}
            />

            <TextField
              label="Sukunimi"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              error={!!errors.lastName}
              helperText={errors.lastName}
              fullWidth
              required
              InputProps={{ sx: { minHeight: 56 } }}
            />

            <TextField
              label="Sähköposti"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              error={!!errors.email}
              helperText={errors.email}
              fullWidth
              required
              InputProps={{ sx: { minHeight: 56 } }}
            />

            <TextField
              label="Puhelinnumero"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              error={!!errors.phone}
              helperText={errors.phone}
              fullWidth
              required
              placeholder="+358401234567"
              InputProps={{ sx: { minHeight: 56 } }}
            />

            <TextField
              label="Telegram"
              value={telegram}
              onChange={(e) => setTelegram(e.target.value)}
              fullWidth
              placeholder="käyttäjänimi ilman @-merkkiä"
              helperText="Valinnainen"
              InputProps={{ sx: { minHeight: 56 } }}
            />

            <TextField
              label="Tuomarivuodet"
              type="text"
              inputMode="numeric"
              value={refereeYears.toString()}
              onChange={(e) => {
                const val = e.target.value.replace(/^0+/, '') || '0'; // Remove leading zeros
                const num = parseInt(val, 10);
                if (!isNaN(num) && num >= 0 && num <= 99) {
                  setRefereeYears(num);
                }
              }}
              onKeyDown={(e) => {
                // Only allow digits, backspace, delete, tab, arrows
                const allowed = /^[0-9]$/.test(e.key) ||
                  ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight'].includes(e.key);
                if (!allowed) {
                  e.preventDefault();
                }
              }}
              fullWidth
              inputProps={{ pattern: '[0-9]*', maxLength: 2 }}
              helperText="Kuinka monta vuotta on toiminut tuomarina"
              InputProps={{ sx: { minHeight: 56 } }}
            />

            <Button
              type="submit"
              variant="contained"
              fullWidth
              size="large"
              startIcon={<PersonAddIcon />}
              disabled={createMutation.isPending}
              sx={{ py: 1.5, mt: 2 }}
            >
              {createMutation.isPending ? 'Luodaan...' : 'Luo käyttäjä'}
            </Button>
          </Box>
        </form>
      </Paper>
    </Box>
  );
}

